import { ChevronRight } from 'lucide-react'

import { convertGrapeMoneyToNumber } from 'utils'
import { type EmployersMemberInvoiceDetail, type Money } from 'utils/web_api.schemas'

interface MemberInvoiceBreakdownMobileProps {
  memberInvoices: EmployersMemberInvoiceDetail[]
  priorMonthName: string
  nextMonthName: string
  formatMoneyValue: (money: Money | undefined) => string
  showPlanYear: boolean
  onMemberClick: (memberInvoiceId: string) => void
  hasDoubleBilling: boolean
}

export function MemberInvoiceBreakdownMobile ({
  memberInvoices,
  priorMonthName,
  nextMonthName,
  formatMoneyValue,
  showPlanYear,
  onMemberClick,
  hasDoubleBilling,
}: MemberInvoiceBreakdownMobileProps) {
  return (
    <div className='mt-4 md:hidden space-y-0'>
      {memberInvoices.map((memberInvoice, index) => (
        <div
          key={memberInvoice.id}
          onClick={() => onMemberClick(memberInvoice.id)}
          className='cursor-pointer'
        >
          {index > 0 && <hr className='border-gray-200 my-4' />}
          <div className='flex items-center mb-3 min-w-0'>
            <span className='body flex items-center flex-shrink-0'>{memberInvoice.full_name || '---'}</span>
            <div className='grow shrink min-w-0 mx-1 sm:mx-2 flex items-center'>
              <svg className='w-full hidden sm:block' height='4' style={{ overflow: 'visible' }}>
                <line
                  x1='0'
                  y1='2'
                  x2='100%'
                  y2='2'
                  stroke='#CFCFCF'
                  strokeWidth='3'
                  strokeLinecap='round'
                  strokeDasharray='0 12'
                />
              </svg>
              <svg className='w-full block sm:hidden' height='2' style={{ overflow: 'visible' }}>
                <line
                  x1='0'
                  y1='1'
                  x2='100%'
                  y2='1'
                  stroke='#CFCFCF'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeDasharray='0 4'
                />
              </svg>
            </div>
            <div className='flex items-center gap-1 sm:gap-2 flex-shrink-0'>
              <span className='body text-right whitespace-nowrap'>{formatMoneyValue(memberInvoice.amounts?.total)}</span>
              <ChevronRight size={16} className='text-gray-400' />
            </div>
          </div>
          <div className='space-y-2'>
            {showPlanYear && (
              <div className='flex justify-between items-center'>
                <span className='body-sm text-gray-500'>Plan Year</span>
                <span className='body-sm text-right mr-2'>
                  {memberInvoice.plan_year ?? '---'}
                </span>
              </div>
            )}
            {hasDoubleBilling &&
            convertGrapeMoneyToNumber(memberInvoice.amounts.double_billing_total) > 0 && (
              <div className='flex justify-between items-center'>
                <span className='body-sm text-gray-500'>
                  Payment for {priorMonthName} insurance coverage
                </span>
                <span className='body-sm text-right mr-2'>
                  {formatMoneyValue(memberInvoice.amounts.double_billing_total)}
                </span>
              </div>
            )}
            <div className='flex justify-between items-center'>
              <span className='body-sm text-gray-500'>
                Pre-payment for {nextMonthName} insurance coverage
              </span>
              <span className='body-sm text-right mr-2'>
                {formatMoneyValue(memberInvoice.amounts?.total_insurance_premiums)}
              </span>
            </div>
            <div className='flex justify-between items-center'>
              <span className='body-sm text-gray-500'>
                Changes in {priorMonthName} insurance coverage
              </span>
              <span className='body-sm text-right mr-2'>
                {formatMoneyValue(
                  memberInvoice.amounts?.coverage_corrections_excluding_double_billing ||
                  memberInvoice.amounts?.coverage_corrections
                )}
              </span>
            </div>
            <div className='flex justify-between items-center'>
              <span className='body-sm text-gray-500'>Qualified medical expenses</span>
              <span className='body-sm text-right mr-2'>
                {formatMoneyValue(memberInvoice.amounts?.other)}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
